import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    IconButton,
    Typography,
    Select,
    MenuItem,
    Snackbar,
    FormControl,
    Alert, Autocomplete, Divider
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { styled, keyframes } from '@mui/system';
import HomeIcon from '@mui/icons-material/Home';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Dialog, DialogTitle, DialogContent, DialogActions, } from '@mui/material';
import { useNavigate } from "react-router-dom";
import { Notyf } from 'notyf';

const notyf = new Notyf({
    position: { x: 'left', y: 'bottom' },
    duration: 3000,
});


const slideInFromLeft = keyframes`
  0% {
    transform: translateX(-20px);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
`;

const AnimatedTypography = styled(Typography)(({ theme }) => ({
    animation: `${slideInFromLeft} 0.5s ease-out`,
}));

const Stock = () => {
    const [user, setUser] = useState({});
    const [token, setToken] = useState(() => localStorage.getItem('token') || null);
    // Fixed: Initial state for rows to use 'status' instead of 'bottleStatus' for consistency.
    const [rows, setRows] = useState([{ product: '', status: '', quantity: '' }]);
    const [branch, setBranch] = useState('');
    const [date, setDate] = useState('');
    const [loading, setLoading] = useState(false);
    const [fetchedData, setFetchedData] = useState([]);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const [materials, setMaterials] = useState([]);


    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };
    const handleSnackbar = (message, severity) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
    };

    useEffect(() => {
        if (token) {
            localStorage.setItem('token', token);
        }
    }, [token]);


    useEffect(() => {
        const fetchProfile = async () => {
            const token = localStorage.getItem("token");

            if (!token) {
                navigate("/"); // Redirect if no token
                return;
            }

            try {
                const response = await axios.get("https://admin.bizstor.lk/public/api/profile", {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setUser(response.data);
            } catch (error) {
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem("token");
                    navigate("/"); // Redirect on 401 error
                }
            }
        };

        fetchProfile();
    }, [navigate]);

    useEffect(() => {
        if (user.id) {
            fetchMaterials();
        }
    }, [user.id]);

    const handleAddRow = () => {
        // Fixed: Ensure new rows also use 'status'.
        setRows([...rows, { product: '', status: '', quantity: '' }]);
    };

    const handleDeleteRow = (index) => {
        const newRows = rows.filter((row, i) => i !== index);
        setRows(newRows);
    };

    const handleInputChange = (index, field, value) => {
        const newRows = rows.map((row, i) => {
            if (i === index) {
                return { ...row, [field]: value };
            }
            return row;
        });
        setRows(newRows);
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await axios.get('https://admin.bizstor.lk/public/api/get-data');
            setFetchedData(response.data);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleSubmit = async () => {
        setLoading(true);

        const hasInvalidRow = rows.some(row => !row.product || !row.quantity || !row.status);

        if (hasInvalidRow) {
            notyf.error('Product, status, and quantity are required!');
            setLoading(false);
            return;
        }

        try {
            const data = {
                usertoken: user.id,
                branch: branch,
                date: date || new Date().toISOString().split('T')[0],
                items: rows.map(row => ({
                    product: row.product, // Stays as 'product' as requested
                    quantity: row.quantity,
                    status: row.status
                }))
            };

            await axios.post('https://admin.bizstor.lk/public/api/save-stock-data', data);

            setBranch('');
            setDate('');
            setRows([{ product: '', quantity: '', status: '' }]);

            notyf.success('Inventory saved successfully!');
            fetchData();
        } catch (error) {
            notyf.error('Something went wrong!');
        } finally {
            setLoading(false);
        }
    };

    const fetchMaterials = () => {
    setLoading(true);

    axios.get('https://admin.bizstor.lk/public/api/get-material')
        .then(response => {
            const allMaterials = response.data.materials || response.data || [];

            if (!Array.isArray(allMaterials)) {
                console.error("Fetched material data is not an array:", allMaterials);
                setMaterials([]);
                return;
            }

            if (user?.id) {
                const filteredMaterials = allMaterials.filter(material =>
                    String(material.usertoken).toLowerCase() === String(user.id).toLowerCase()
                );
                setMaterials(filteredMaterials);
            } else {
                setMaterials([]);
                console.warn("User ID is not available.");
            }
        })
        .catch(error => {
            console.error('Error fetching material data:', error);
            setMaterials([]);
        })
        .finally(() => {
            setLoading(false);
        });
};

    
    // Fixed: Correctly derive material options from the 'materials' state using useMemo.
    const materialOptions = useMemo(() => materials.map(m => m.material), [materials]);

    return (
        <Box
            sx={{
                mt: 10,
                mb: 5
            }}
        >
            <AnimatedTypography
                component="h1"
                sx={{
                    ml: { xs: 2, sm: 3 },
                    mt: { xs: 2, sm: 3 },
                    color: '',
                    letterSpacing: '0.01em',
                    fontSize: { xs: '20px', sm: '24px' },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <HomeIcon
                        sx={{
                            ml: 1,
                            fontSize: '18px',
                            color: '#6d7081',
                        }}
                    />
                    <Typography
                        component="span"
                        sx={{
                            ml: 0.5,
                            fontSize: '14px',
                            color: '#6d7081',
                        }}
                    >
                        - Inventory - Material
                    </Typography>
                </Box>
            </AnimatedTypography>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: {sm:'95%',xs:'90%'},
                    maxWidth: 'auto',
                    margin: 'auto',
                    padding: '10px',
                    bgcolor: 'background.paper',
                    border: '1px solid #EDF2F7',
                    borderRadius: '16px',
                    marginTop: '30px',
                    position: 'relative',
                    '@media (max-width: 600px)': {
                        marginTop: '10px',
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        '@media (max-width: 600px)': {
                            margin: 'auto',
                        },
                        color: '#1e293b',
                        ml: 2
                    }}
                >
                    <h2>Material</h2>
                </Box>
                <TableContainer>
                    <Table>
                        <TableBody>
                            <TableRow>
                                <TableCell>
                                    <TextField
                                        type="date"
                                        value={date || new Date().toISOString().split('T')[0]}
                                        onChange={(e) => setDate(e.target.value)}
                                        variant="outlined"
                                        fullWidth
                                        size="small"
                                        sx={{
                                            width: '280px',
                                            justifyContent: 'center',
                                        }}
                                    />
                                    <IconButton onClick={handleClickOpen}>
                                        <InfoOutlinedIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'right',
                        justifyContent: 'flex-end',
                        marginTop: '10px',
                        textAlign: 'right',
                    }}
                >
                    <Typography variant="subtitle1">
                        TOTAL QTY
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                        {rows.reduce((total, row) => total + (parseInt(row.quantity, 10) || 0), 0)}
                    </Typography>
                </Box>

                <TableContainer
                    style={{
                        marginTop: '20px',
                        width: '100%',
                        overflowX: 'auto',
                    }}
                >
                    <Table style={{ minWidth: 650 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Material</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Qty</TableCell>
                                <TableCell>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row, index) => (
                                <TableRow key={index}>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>
                                        <FormControl fullWidth>
                                             {/* Fixed: Autocomplete now uses materialOptions and updates row.product */}
                                            <Autocomplete
                                                value={row.product}
                                                onChange={(event, newValue) => {
                                                    handleInputChange(index, 'product', newValue || '');
                                                }}
                                                options={materialOptions}
                                                getOptionLabel={(option) => option}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        size="small"
                                                        sx={{
                                                            width: '400px',
                                                            '@media (max-width: 600px)': {
                                                                width: '250px',
                                                            },
                                                        }}
                                                    />
                                                )}
                                                ListboxComponent={React.forwardRef((props, ref) => (
                                                    <ul ref={ref} {...props}>
                                                        {props.children}
                                                        <Divider sx={{ my: 0.5 }} />
                                                        <MenuItem>
                                                            <Link to="/addmaterial" style={{ textDecoration: 'none', color: 'inherit', width: '100%' }}>
                                                                + Add New Material
                                                            </Link>
                                                        </MenuItem>
                                                    </ul>
                                                ))}
                                            />
                                        </FormControl>
                                    </TableCell>
                                    <TableCell>
                                        <Select
                                            value={row.status}
                                            onChange={(e) => handleInputChange(index, 'status', e.target.value)}
                                            variant="outlined"
                                            size="small"
                                            sx={{
                                                width: '200px',
                                                '@media (max-width: 600px)': {
                                                    width: '150px',
                                                },
                                            }}
                                        >
                                            <MenuItem value="Filled">Used</MenuItem>
                                            <MenuItem value="Empty">In Stock</MenuItem>
                                        </Select>
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            type="number"
                                            value={row.quantity}
                                            onChange={(e) => handleInputChange(index, 'quantity', e.target.value)}
                                            variant="outlined"
                                            size="small"
                                            sx={{ width: '150px' }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <IconButton onClick={() => handleDeleteRow(index)} aria-label="delete" color="inherit">
                                            <DeleteIcon
                                                sx={{
                                                    '&:hover': {
                                                        color: 'red',
                                                    },
                                                }}
                                            />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box display="flex" justifyContent="flex-start" marginTop="15px">
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={handleAddRow}
                        style={{ backgroundColor: 'black', color: 'white' }}
                    >
                        <PlaylistAddIcon />
                    </Button>
                </Box>

                <Box style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={loading}
                        style={{
                            backgroundColor: '#4287f5',
                            color: 'white',
                            marginTop: '30px',
                            borderRadius: '20px',
                            padding: '8px 24px',
                            '&:hover': {
                                backgroundColor: '#356bc4',
                            },
                            marginBottom: { xs: '8px', sm: '0' }
                        }}
                    >
                        Submit
                    </Button>
                </Box>

                {loading && (
                    <Box sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'white',
                        zIndex: 9999
                    }}>
                        <l-trio
                            size="50"
                            speed="1.3"
                            color="red"
                        ></l-trio>
                    </Box>
                )}

                <Dialog open={open} onClose={handleClose}>
                    <DialogTitle>Instructions</DialogTitle>
                    <DialogContent>
                        <Typography>This is used to store the materials.</Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose} color="primary">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={6000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                >
                    <Alert
                        onClose={handleSnackbarClose}
                        severity={snackbarSeverity}
                        sx={{ width: '100%' }}
                    >
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            </Box>
        </Box>
    );
};

export default Stock;